/**
 * Client Options — API client wrappers (SP13.0).
 *
 * Wraps the three /api/client-options/* endpoints and isolates the
 * AllowAutomaticPrinting UI inversion (D15) at the boundary so dialog code
 * works in UI-natural terms (`displayAllDocuments`).
 */

import apiClient from '@/api/client';
import { getMachineId } from '@/lib/machine-fingerprint';
import type {
  ClientOptions,
  ClientOptionsLookupRow,
  ClientOptionsWithSystemFlags,
} from './types';

export interface ClientOptionsDialogState
  extends Omit<ClientOptionsWithSystemFlags, 'allowAutomaticPrinting'> {
  /**
   * UI-facing inverse of the stored `allowAutomaticPrinting` bit.
   * Checked = "Display all documents after transactions"
   *         = `!allowAutomaticPrinting` (analysis §3 / D15).
   */
  displayAllDocuments: boolean;
}

const fromWire = (wire: ClientOptionsWithSystemFlags): ClientOptionsDialogState => {
  const { allowAutomaticPrinting, ...rest } = wire;
  return {
    ...rest,
    displayAllDocuments: !allowAutomaticPrinting,
  };
};

const toWire = (form: ClientOptionsDialogState): ClientOptions => {
  // useDigitalSignatures and allowTipBoxTransactions are response-only system
  // flags — the dialog reads them to gate controls but they are not sent on
  // save. Drop them off the wire payload via underscore-prefixed aliases.
  const {
    displayAllDocuments,
    useDigitalSignatures: _useDigitalSignatures,
    allowTipBoxTransactions: _allowTipBoxTransactions,
    ...rest
  } = form;
  return {
    ...rest,
    allowAutomaticPrinting: !displayAllDocuments,
    // Sentinel normalize (D20) — empty string in any nullable text field is
    // sent as null. Vault GUIDs use '' as the "(Not Selected)" sentinel in
    // the <select> options, so they get normalized too.
    fundingVaultTypeId: rest.fundingVaultTypeId || null,
    lockedCashVaultTypeId: rest.lockedCashVaultTypeId || null,
    lockedChipVaultTypeId: rest.lockedChipVaultTypeId || null,
    lockedPromoChipVaultTypeId: rest.lockedPromoChipVaultTypeId || null,
    description: rest.description || null,
    fullSizePrinterName: rest.fullSizePrinterName || null,
    receiptSizePrinterName: rest.receiptSizePrinterName || null,
    checkPrinterName: rest.checkPrinterName || null,
    barcodePrefix: rest.barcodePrefix || null,
    barcodeSuffix: rest.barcodeSuffix || null,
    currencyCounterProvider: rest.currencyCounterProvider || null,
    currencyCounterIp: rest.currencyCounterIp || null,
    comparisonToolFilePath: rest.comparisonToolFilePath || null,
    comparisonToolTextComparisonArguments:
      rest.comparisonToolTextComparisonArguments || null,
    comparisonToolTableComparisonArguments:
      rest.comparisonToolTableComparisonArguments || null,
  };
};

export async function getCurrentClientOptions(): Promise<ClientOptionsDialogState> {
  const machineId = await getMachineId();
  const response = await apiClient.post<ClientOptionsWithSystemFlags>(
    '/api/client-options/current',
    { machineId },
  );
  return fromWire(response.data);
}

export async function listAllClientOptions(): Promise<ClientOptionsLookupRow[]> {
  const response = await apiClient.get<ClientOptionsLookupRow[]>(
    '/api/client-options/list',
  );
  return response.data;
}

export async function saveClientOptions(form: ClientOptionsDialogState): Promise<void> {
  const payload = toWire(form);
  await apiClient.post('/api/client-options/save', payload, {
    headers: {
      // SP9.7 — auto-generate per-call key so retries dedupe server-side.
      'Idempotency-Key': crypto.randomUUID(),
    },
  });
}

// Exposed for unit testing.
export const _converter = { fromWire, toWire };
